import * as Sentry from '@sentry/browser';
import type { Reducer } from '@reduxjs/toolkit';

import { maybeLogNetworkError } from '../Utils/flattenError';
import type { AppError, EventName } from '../Utils/types';
import createUuid from '../Utils/createUuid';
import { getReducerError, type ReducerError, resetReducerErrors } from '../Utils/reducerError';
import type { ResourceErrorInterface } from 'tg-resources';

export const CHAT_TAB: string = 'chat';
export const CASE_TAB: string = 'case';
export const TEAMS_TAB: string = 'teams';
export const ANALYTICS_TAB: string = 'analytics';

export type ScreenBreakpoint = 'mobile' | 'tablet' | 'laptop' | 'desktop';

export const DONOR_STATUS_VIEW: string = 'status';
export const DONOR_TASK_VIEW: string = 'task';

export const DONOR_TASK_DUE_VIEW: string = 'taskdue';
export const DONOR_ADD_PEOPLE_VIEW: string = 'people';
export const DONOR_ROOMS_VIEW: string = 'rooms';
export const DONOR_TRACKER_VIEW: string = 'tracker';
export const DONOR_FOLLOWER_GROUP_VIEW: string = 'follower';
export const DONOR_CASE_ADMIN_INFO_VIEW: string = 'admin-info';
export const DONOR_PRINT_VIEW: string = 'print';
export const DONOR_SERVICE_ALERTS_VIEW: string = 'alerts';
export const DONOR_SERVICE_ALERTS_DETAIL_VIEW: string = 'alerts-detail';
export const CASE_SERVICE_XMAT_DEVICE: string = 'case-xmat';
export const CASE_SERVICE_INSIGHTS_DEVICE: string = 'case-insights';
export const CASE_TIMELINE: string = 'timeline';
export const UPDATE_DONOR_VIEW: string = 'update-donor';

// Define the backoff (in seconds) for persistent reconnect attempts
export const RECONNECT_DELAYS_SECONDS = [5, 10, 15, 30, 60];

type ApplicationState = {
  errors: Array<AppError>;
  deviceToken?: string;
  sagaHeading?: string;
  sagaMessage?: string;
  sagaLabel?: string;
  isDialog?: boolean;
  reducerErrors: Array<ReducerError>;
  selectedTab: string;
  donorView: string;
  isPriority: boolean;
  screenBreakpoint: ScreenBreakpoint;

  nextReconnectAttemptIn?: number;
};

const initialState: ApplicationState = {
  errors: [],
  deviceToken: undefined,
  sagaHeading: '',
  sagaMessage: '',
  sagaLabel: '',
  isDialog: false,
  reducerErrors: [],
  selectedTab: 'chat',
  donorView: 'status',
  isPriority: false,
  screenBreakpoint: 'mobile',

  nextReconnectAttemptIn: undefined,
};

export type PushErrorAction = { type: 'Application/PUSH_ERROR'; message: string; label: string };
export type PopErrorAction = { type: 'Application/POP_ERROR' };

// Device token maintenance
export type SetDeviceToken = { type: 'Application/SET_DEVICE_TOKEN'; deviceToken: string };
export type StoreDeviceToken = { type: 'Application/STORE_DEVICE_TOKEN'; deviceToken: string };

export type LogFirebaseEvent = { type: 'Application/LOG_EVENT'; eventName: EventName };

export type ResetData = { type: 'Application/RESET_DATA' };

export type Reload = { type: 'App/RELOAD' };

export type SetNextReconnectAttemptIn = { type: 'Application/SET_NEXT_RECONNECT_ATTEMPT_IN'; seconds?: number };

export type SetSagaMessage = {
  type: 'Application/SET_SAGA_MESSAGE';
  heading: string;
  message: string;
  label: string;
  isDialog: boolean;
  isPriority: boolean;
};
export type ReportError = { type: 'Application/REPORT_ERROR'; reducerError: ReducerError };
export type ResetError = { type: 'Application/RESET_ERROR'; uuid: string };

export type GetApplicationData = {
  type: 'Application/GET_APPLICATION_DATA';
  getOrganizations: boolean;
  getChatrooms: boolean;
  getChatroomMembers: boolean;
  getInstantMessages: boolean;
  chatroomIds: Array<number>;
};

export type SelectTab = { type: 'Application/SELECT_TAB'; tabName: string };
export type SelectDonorView = { type: 'Application/SELECT_DONOR_VIEW'; viewName: string };
export type SetScreenBreakpoint = { type: 'Application/SET_SCREEN_BREAKPOINT'; screenBreakpoint: ScreenBreakpoint };

export type RedirectToAdminPortal = { type: 'Application/REDIRECT_TO_ADMIN_PORTAL' };
export type TestAdminPortalEndpoint = { type: 'Application/TEST_ADMIN_PORTAL_ENDPOINT' };

export type ApplicationAction =
  | SetDeviceToken
  | PushErrorAction
  | PopErrorAction
  | StoreDeviceToken
  | LogFirebaseEvent
  | SetSagaMessage
  | GetApplicationData
  | Reload
  | ResetData
  | ReportError
  | ResetError
  | SelectTab
  | SelectDonorView
  | SetScreenBreakpoint
  | SetNextReconnectAttemptIn
  | RedirectToAdminPortal
  | TestAdminPortalEndpoint;

export const storeDeviceToken = (deviceToken: string): StoreDeviceToken => ({
  type: 'Application/STORE_DEVICE_TOKEN',
  deviceToken,
});

export const testAdminPortalEndpoint = (): TestAdminPortalEndpoint => ({
  type: 'Application/TEST_ADMIN_PORTAL_ENDPOINT',
});

export const setDeviceToken = (deviceToken: string): SetDeviceToken => ({
  type: 'Application/SET_DEVICE_TOKEN',
  deviceToken,
});

export const setNextReconnectAttemptIn = (seconds?: number): SetNextReconnectAttemptIn => ({
  type: 'Application/SET_NEXT_RECONNECT_ATTEMPT_IN',
  seconds,
});

export const logFirebaseEvent = (eventName: EventName): LogFirebaseEvent => ({
  type: 'Application/LOG_EVENT',
  eventName,
});

export const setSagaMessage = (
  heading: string,
  message: string,
  label: string,
  isDialog: boolean = false,
  isPriority: boolean = false,
): SetSagaMessage => ({
  type: 'Application/SET_SAGA_MESSAGE',
  heading,
  message,
  label,
  isDialog,
  isPriority,
});

export const reportError = (reducerError: ReducerError): ReportError => ({
  type: 'Application/REPORT_ERROR',
  reducerError,
});

export const resetError = (uuid: string): ResetError => ({
  type: 'Application/RESET_ERROR',
  uuid,
});

export const getApplicationData = (
  getOrganizations: boolean,
  getChatrooms: boolean,
  getChatroomMembers: boolean,
  getInstantMessages: boolean,
  chatroomIds: Array<number>,
): GetApplicationData => ({
  type: 'Application/GET_APPLICATION_DATA',
  getOrganizations,
  getChatrooms,
  getChatroomMembers,
  getInstantMessages,
  chatroomIds,
});

export const pushError = (
  message: string,
  error?: Error | ResourceErrorInterface,
  label: string = 'Error',
): PushErrorAction => {
  if (error) {
    // Ensure we log the error properly to sentry
    if (!maybeLogNetworkError(error)) {
      Sentry.captureException(error, {
        extra: { message, errorMsg: 'A network error occurred that was not handled in `maybeLogNetworkError`' },
      });
    }

    if (import.meta.env.MODE === 'development') {
      console.warn('Error occurred', error);
    }
  }

  return {
    type: 'Application/PUSH_ERROR',
    message,
    label,
  };
};

export const popError = (): PopErrorAction => ({
  type: 'Application/POP_ERROR',
});

export const selectTab = (tabName: string): SelectTab => ({
  type: 'Application/SELECT_TAB',
  tabName,
});

export const selectDonorView = (viewName: string): SelectDonorView => ({
  type: 'Application/SELECT_DONOR_VIEW',
  viewName,
});

export const setScreenBreakpoint = (screenBreakpoint: ScreenBreakpoint): SetScreenBreakpoint => ({
  type: 'Application/SET_SCREEN_BREAKPOINT',
  screenBreakpoint,
});

export const redirectToAdminPortal = (): RedirectToAdminPortal => ({
  type: 'Application/REDIRECT_TO_ADMIN_PORTAL',
});

export const reloadApp = (): Reload => ({
  type: 'App/RELOAD',
});

const reducer = (state: ApplicationState = initialState, action: ApplicationAction): ApplicationState => {
  // wrap the whole reducer to catch any unexpected exceptions and record them
  // Another component will need to act upon these and potentially send them to Sentry
  try {
    return innerReducer(state, action);
  } catch (err) {
    const reducerErrors: ReducerError[] = state.reducerErrors.slice();
    const uuid = createUuid(0);
    if (err instanceof Error) {
      reducerErrors.push(getReducerError(uuid, 'application', action, err));
    } else {
      const unknownError = new Error('An unknown error occurred in ApplicationActions');
      reducerErrors.push(getReducerError(uuid, 'application', action, unknownError));
    }

    return {
      ...state,

      reducerErrors,
    };
  }
};

const innerReducer = (state: ApplicationState = initialState, action: ApplicationAction): ApplicationState => {
  let errors: AppError[];

  switch (action.type) {
    case 'Application/RESET_ERROR': {
      const reducerErrors = resetReducerErrors(state.reducerErrors, action);
      return {
        ...state,
        reducerErrors,
      };
    }

    case 'Application/SET_DEVICE_TOKEN':
      return {
        ...state,

        deviceToken: action.deviceToken,
      };

    case 'Application/SELECT_TAB':
      return {
        ...state,

        selectedTab: action.tabName,
      };

    case 'Application/SELECT_DONOR_VIEW':
      return {
        ...state,

        donorView: action.viewName,
      };

    case 'Application/SET_SCREEN_BREAKPOINT':
      return {
        ...state,

        screenBreakpoint: action.screenBreakpoint,
      };

    case 'Application/PUSH_ERROR':
      errors = [
        {
          message: action.message,
          label: action.label,
        },
      ].concat(state.errors.slice());

      return {
        ...state,

        errors,
      };

    case 'Application/POP_ERROR':
      return {
        ...state,

        errors: state.errors.slice(0, state.errors.length - 1),
      };

    case 'Application/SET_SAGA_MESSAGE':
      return {
        ...state,

        sagaHeading: action.heading,
        sagaMessage: action.message,
        sagaLabel: action.label,
        isDialog: action.isDialog,
        isPriority: action.isPriority,
      };

    case 'Application/SET_NEXT_RECONNECT_ATTEMPT_IN':
      return {
        ...state,

        nextReconnectAttemptIn: action.seconds,
      };

    default:
      return state;
  }
};

export default reducer as Reducer<ApplicationState>;
